const CALCOM_BASE_URL = String(
  process.env.REACT_APP_CALCOM_BASE_URL
  || process.env.REACT_APP_BOOKING_PROVIDER_BASE_URL
  || 'https://cal.com'
).trim().replace(/\/+$/, '');

const CALCOM_USERNAME = String(
  process.env.REACT_APP_CALCOM_USERNAME
  || ''
).trim();

const CALCOM_EVENT_TYPE_SLUG = String(
  process.env.REACT_APP_CALCOM_EVENT_TYPE_SLUG
  || process.env.REACT_APP_BOOKING_EVENT_TYPE
  || 'book-a-meeting'
).trim();

const CALCOM_PUBLIC_EVENT_URL = String(
  process.env.REACT_APP_CALCOM_PUBLIC_EVENT_URL
  || process.env.REACT_APP_BOOKING_PUBLIC_LINK
  || ''
).trim();

const CALCOM_NAMESPACE = String(
  process.env.REACT_APP_CALCOM_NAMESPACE
  || ''
).trim();

const CALCOM_MODE = String(
  process.env.REACT_APP_CALCOM_MODE
  || 'public_link'
).trim().toLowerCase();

const BOOKING_PAYMENT_MODE = String(
  process.env.REACT_APP_BOOKING_PAYMENT_MODE
  || 'carrierflow_paypal'
).trim().toLowerCase();

const STRIPE_PUBLISHABLE_KEY = String(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  || ''
).trim();

const STRIPE_PRICE_ID = String(
  process.env.REACT_APP_STRIPE_PRICE_ID
  || ''
).trim();

const STRIPE_PAYMENT_LINK_URL = String(
  process.env.REACT_APP_STRIPE_PAYMENT_LINK_URL
  || ''
).trim();
const ALLOWED_CALCOM_HOSTS = new Set(
  String(
    process.env.REACT_APP_CALCOM_ALLOWED_HOSTS
    || process.env.REACT_APP_TRUSTED_CALCOM_HOSTS
    || ''
  )
    .split(',')
    .map((value) => sanitizeText(value).toLowerCase())
    .filter(Boolean)
);

function sanitizeText(value) {
  return String(value || '').trim();
}

function sanitizeHost(value) {
  const raw = sanitizeText(value).toLowerCase();
  return raw ? raw.replace(/^\./, '') : '';
}

function isTrustedCalComHost(hostname) {
  const normalized = sanitizeHost(hostname);
  if (!normalized) {
    return false;
  }
  if (ALLOWED_CALCOM_HOSTS.has(normalized)) {
    return true;
  }

  return normalized === 'cal.com' || normalized.endsWith('.cal.com');
}

function buildSafeUrl(rawUrl) {
  const normalized = sanitizeText(rawUrl);
  if (!normalized) {
    return null;
  }

  try {
    const parsed = new URL(normalized);
    const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(
      sanitizeHost(parsed.hostname)
    );
    if (parsed.protocol !== 'https:' && !(parsed.protocol === 'http:' && isLocalhost)) {
      return null;
    }
    if (!isTrustedCalComHost(parsed.hostname)) {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
}

function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (error) {
    return undefined;
  }
}

function ensureLeadingSlash(value) {
  if (!value) {
    return '';
  }
  return value.startsWith('/') ? value : `/${value}`;
}

function buildCalComPath(eventTypeSlug = CALCOM_EVENT_TYPE_SLUG) {
  if (CALCOM_PUBLIC_EVENT_URL) {
    const parsedPublicEventUrl = buildSafeUrl(CALCOM_PUBLIC_EVENT_URL);
    if (!parsedPublicEventUrl) {
      return '';
    }
    return parsedPublicEventUrl.toString().replace(/\/+$/, '');
  }

  const parsedBaseUrl = buildSafeUrl(CALCOM_BASE_URL);
  if (!parsedBaseUrl) {
    return '';
  }

  if (!CALCOM_USERNAME || !eventTypeSlug) {
    return '';
  }

  const namespacePath = CALCOM_NAMESPACE ? `${ensureLeadingSlash(CALCOM_NAMESPACE)}` : '';
  return `${parsedBaseUrl.origin}${namespacePath}/${CALCOM_USERNAME}/${eventTypeSlug}`;
}

export function buildCalComPublicBookingUrl(options = {}) {
  const baseUrl = buildCalComPath(options.eventTypeSlug || CALCOM_EVENT_TYPE_SLUG);
  if (!baseUrl) {
    return '';
  }

  const url = new URL(baseUrl);
  const timezone = sanitizeText(options.timezone || getBrowserTimezone());
  const name = sanitizeText(options.name || options.contactName);
  const email = sanitizeText(options.email);
  const company = sanitizeText(options.company || options.companyName);
  const notes = sanitizeText(options.notes);
  const start = sanitizeText(options.start);
  const end = sanitizeText(options.end);
  const paymentMode = sanitizeText(options.paymentMode || BOOKING_PAYMENT_MODE) || 'carrierflow_paypal';

  if (timezone) url.searchParams.set('timezone', timezone);
  if (name) url.searchParams.set('name', name);
  if (email) url.searchParams.set('email', email);
  if (notes) url.searchParams.set('notes', notes);
  if (company) url.searchParams.set('company', company);
  if (start) url.searchParams.set('start', start);
  if (end) url.searchParams.set('end', end);
  if (paymentMode) url.searchParams.set('bookingPaymentMode', paymentMode);

  Object.entries(options.query || {}).forEach(([key, value]) => {
    const normalizedValue = sanitizeText(value);
    if (normalizedValue) {
      url.searchParams.set(key, normalizedValue);
    }
  });

  return url.toString();
}

export function getCalComEmbedConfig(overrides = {}) {
  return {
    provider: 'calcom',
    mode: sanitizeText(overrides.mode || CALCOM_MODE) || 'public_link',
    baseUrl: sanitizeText(overrides.baseUrl || CALCOM_BASE_URL) || 'https://cal.com',
    username: sanitizeText(overrides.username || CALCOM_USERNAME),
    namespace: sanitizeText(overrides.namespace || CALCOM_NAMESPACE),
    eventTypeSlug: sanitizeText(overrides.eventTypeSlug || CALCOM_EVENT_TYPE_SLUG) || 'book-a-meeting',
    publicEventUrl: sanitizeText(overrides.publicEventUrl || CALCOM_PUBLIC_EVENT_URL),
    paymentMode: sanitizeText(overrides.paymentMode || BOOKING_PAYMENT_MODE) || 'carrierflow_paypal',
    prefillUrl: buildCalComPublicBookingUrl(overrides.prefill || {}),
  };
}

export function getBookingPaymentRuntimeConfig() {
  return {
    paymentMode: BOOKING_PAYMENT_MODE,
    stripePublishableKey: STRIPE_PUBLISHABLE_KEY,
    stripePriceId: STRIPE_PRICE_ID,
    stripePaymentLinkUrl: STRIPE_PAYMENT_LINK_URL,
    usesStripe: BOOKING_PAYMENT_MODE === 'calcom_stripe' || Boolean(STRIPE_PRICE_ID || STRIPE_PAYMENT_LINK_URL),
  };
}
